import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Minus, Plus, ShoppingBag, Trash2, Truck, Mail, BadgePercent } from "lucide-react";
import axios from "axios";
import { useCart } from "../context/CartContext";
import { useProducts } from "../hooks/useProducts";
import { API_URL, formatPrice } from "../config/site";

export const CartDrawer = () => {
  const { items, open, setOpen, setQuantity, remove, total, add } = useCart();
  const { products } = useProducts();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [email, setEmail] = useState("");
  const [notice, setNotice] = useState(null);
  const hasKit = items.some((i) => i.slug.startsWith("kit-"));
  const federa = products.find((p) => p.slug === "fibra-di-seta");
  const showFederaSuggestion = hasKit && federa && !items.some((i) => i.slug === "fibra-di-seta");

  const checkout = async () => {
    const cleanEmail = email.trim().toLowerCase();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(cleanEmail)) {
      setError("Inserisci un indirizzo email valido per continuare.");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const { data } = await axios.post(`${API_URL}/payments/checkout`, {
        items: items.map((i) => ({ slug: i.slug, quantity: i.quantity })),
        origin_url: window.location.origin,
        customer_email: cleanEmail,
      });
      if (data.discount_applied === false) {
        setNotice("Bentornata! Lo sconto del 10% è riservato al primo ordine e risulta già utilizzato con questa email.");
        setTimeout(() => {
          window.location.href = data.checkout_url;
        }, 2600);
      } else {
        window.location.href = data.checkout_url;
      }
    } catch (e) {
      setError("Qualcosa è andato storto. Riprova tra un istante.");
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="cart-drawer"
          className="fixed inset-0 z-[80]"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <div className="absolute inset-0 bg-plum/30 backdrop-blur-sm" onClick={() => setOpen(false)} />
          <motion.aside
            className="absolute right-0 top-0 bottom-0 w-full max-w-md bg-white shadow-2xl flex flex-col"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", damping: 30, stiffness: 280 }}
          >
            <div className="flex items-center justify-between p-5 border-b border-lilac-light">
              <h2 className="font-serif text-xl text-plum flex items-center gap-2">
                <ShoppingBag className="w-5 h-5" /> Il tuo carrello
              </h2>
              <button data-testid="cart-close-btn" onClick={() => setOpen(false)} className="p-2 text-plum" aria-label="Chiudi carrello">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-5 space-y-4">
              {items.length === 0 && (
                <div className="text-center pt-16" data-testid="cart-empty-state">
                  <p className="font-serif text-xl text-plum">Il carrello è vuoto</p>
                  <p className="text-sm text-plum/50 mt-2">Scopri il Trattamento Rapida Crescita e inizia il tuo percorso.</p>
                </div>
              )}
              {items.map((item) => (
                <motion.div
                  key={item.slug}
                  layout
                  data-testid={`cart-item-${item.slug}`}
                  className="flex gap-4 bg-mist rounded-2xl p-3"
                >
                  <img src={item.image} alt={item.name} className="w-20 h-20 rounded-xl object-cover" />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm text-plum leading-snug">{item.name}</p>
                    <p className="text-xs text-plum/50 mt-0.5">{item.format}</p>
                    <div className="flex items-center justify-between mt-2">
                      <div className="flex items-center gap-2 bg-white rounded-full px-2 py-1">
                        <button
                          data-testid={`qty-minus-${item.slug}`}
                          onClick={() => setQuantity(item.slug, item.quantity - 1)}
                          className="p-1 text-plum hover:text-lilac-dark"
                          aria-label="Riduci quantità"
                        >
                          <Minus className="w-3.5 h-3.5" />
                        </button>
                        <span className="text-sm font-semibold w-5 text-center" data-testid={`qty-${item.slug}`}>{item.quantity}</span>
                        <button
                          data-testid={`qty-plus-${item.slug}`}
                          onClick={() => setQuantity(item.slug, item.quantity + 1)}
                          className="p-1 text-plum hover:text-lilac-dark"
                          aria-label="Aumenta quantità"
                        >
                          <Plus className="w-3.5 h-3.5" />
                        </button>
                      </div>
                      <span className="font-serif text-plum">{formatPrice(item.price * item.quantity)}</span>
                    </div>
                  </div>
                  <button
                    data-testid={`remove-${item.slug}`}
                    onClick={() => remove(item.slug)}
                    className="self-start p-1.5 text-plum/40 hover:text-red-500 transition-colors"
                    aria-label="Rimuovi"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </motion.div>
              ))}
            </div>

            {items.length > 0 && (
              <div className="p-5 border-t border-lilac-light space-y-4">
                {showFederaSuggestion && (
                  <div data-testid="federa-suggestion" className="bg-lilac-soft border border-lilac-light rounded-2xl p-4 flex items-center gap-3">
                    <img src={federa.image} alt={federa.name} className="w-14 h-14 rounded-xl object-cover" />
                    <div className="flex-1 min-w-0">
                      <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-matcha">Completa il rituale notturno</p>
                      <p className="text-sm font-semibold text-plum leading-snug">{federa.name} · {formatPrice(federa.price)}</p>
                      <p className="text-xs text-plum/50">Il trattamento di bellezza che lavora mentre dormi.</p>
                    </div>
                    <button
                      data-testid="federa-suggestion-add-btn"
                      onClick={() => add(federa)}
                      className="shrink-0 px-4 py-2.5 rounded-full bg-lilac text-white text-[10px] font-bold uppercase tracking-[0.12em] hover:bg-lilac-dark active:scale-95 transition-all duration-300"
                    >
                      Aggiungi
                    </button>
                  </div>
                )}
                {items.some((i) => i.slug === "kit-full-transformation") ? (
                  <div data-testid="free-shipping-unlocked" className="bg-matcha/10 border border-matcha/40 rounded-2xl p-4 flex items-center gap-3">
                    <span className="w-10 h-10 rounded-full bg-matcha flex items-center justify-center shrink-0">
                      <Truck className="w-5 h-5 text-white" />
                    </span>
                    <div>
                      <p className="text-sm font-bold text-plum">Spedizione gratuita sbloccata</p>
                      <p className="text-xs text-plum/60 leading-relaxed">Hai scelto il percorso Full Transformation di 3 mesi: la spedizione la offriamo noi.</p>
                    </div>
                  </div>
                ) : (
                  <div data-testid="free-shipping-progress" className="bg-lilac-soft border border-lilac-light rounded-2xl p-4">
                    <p className="text-xs text-plum/70 leading-relaxed">
                      Con il percorso <span className="font-bold text-plum">Full Transformation — 3 mesi</span> la spedizione è <span className="font-bold text-matcha">gratuita</span>.
                    </p>
                    <div className="mt-3 h-2 rounded-full bg-white overflow-hidden">
                      <motion.div
                        className="h-full bg-lilac rounded-full"
                        initial={{ width: 0 }}
                        animate={{ width: `${Math.min((total / 98.7) * 100, 100)}%` }}
                        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                      />
                    </div>
                  </div>
                )}
                <div className="flex justify-between items-baseline">
                  <span className="text-sm text-plum/60">Subtotale</span>
                  <span className="font-serif text-2xl text-plum" data-testid="cart-subtotal">{formatPrice(total)}</span>
                </div>
                <p className="text-xs text-plum/50">Spedizione €6,90 · gratuita con il percorso Full Transformation di 3 mesi. IVA calcolata al checkout.</p>
                <div>
                  <label htmlFor="checkout-email" className="text-[10px] font-bold uppercase tracking-[0.14em] text-plum/60 flex items-center gap-1.5 mb-2">
                    <Mail className="w-3.5 h-3.5" /> La tua email
                  </label>
                  <input
                    id="checkout-email"
                    data-testid="checkout-email-input"
                    type="email"
                    value={email}
                    onChange={(e) => { setEmail(e.target.value); setError(null); setNotice(null); }}
                    placeholder="nome@email.it"
                    autoComplete="email"
                    className="w-full rounded-2xl border border-lilac-light bg-white px-4 py-3 text-sm text-plum placeholder:text-plum/30 focus:outline-none focus:ring-2 focus:ring-lilac/60"
                  />
                  <p className="mt-2 text-xs text-plum/50 leading-relaxed flex items-start gap-1.5">
                    <BadgePercent className="w-3.5 h-3.5 mt-0.5 shrink-0 text-matcha" />
                    Se è il tuo primo ordine, il 10% di sconto viene applicato automaticamente a questa email.
                  </p>
                </div>
                {notice && (
                  <div data-testid="checkout-discount-notice" className="bg-lilac-soft border border-lilac-light rounded-2xl p-3">
                    <p className="text-xs text-plum leading-relaxed">{notice}</p>
                  </div>
                )}
                {error && <p className="text-xs text-red-500" data-testid="checkout-error">{error}</p>}
                <button
                  data-testid="checkout-btn"
                  onClick={checkout}
                  disabled={loading}
                  className="w-full py-4 rounded-full bg-lilac text-white font-bold text-sm uppercase tracking-[0.14em] hover:bg-lilac-dark active:scale-[0.98] transition-all duration-300 disabled:opacity-60"
                >
                  {loading ? "Reindirizzamento..." : "Vai al checkout sicuro"}
                </button>
              </div>
            )}
          </motion.aside>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
